import i18n from '../i18n'

/**
 * The team card's rules (V4-w), pure so both pages and the tests share them.
 * Input is the `teams_mine` answer as the wire sends it, or null while it has
 * not arrived / could not be read.
 */

/**
 * What the card should be:
 * - `hidden` — nothing at all: the answer has not arrived, the call failed,
 *   or teams are switched off (`enabled: false`, the default until stage 3).
 *   A learner on today's production therefore sees exactly the page they saw
 *   before.
 * - `none` — teams are on but the learner has no seat yet (before
 *   onboarding, or left out of this month's build as inactive).
 * - `team` — the team and its members.
 */
export function teamCardState(answer) {
  if (!answer || answer.enabled !== true) return 'hidden'
  if (!answer.team || !Array.isArray(answer.team.members)) return 'none'
  return 'team'
}

/** The team's name in the app's language — the server names every team in both. */
export function teamName(team, language = i18n.language) {
  if (!team) return ''
  if (language === 'en') return team.nameEn || team.name
  return team.name || team.nameEn
}

/**
 * Whole days until `periodEndsAt` (when teams are rebuilt), never negative;
 * null for a missing or unreadable instant. Rounded up: a period ending
 * tonight still has «۱ روز».
 */
export function daysLeft(periodEndsAt, now = new Date()) {
  if (!periodEndsAt) return null
  const end = new Date(periodEndsAt).getTime()
  if (Number.isNaN(end)) return null
  return Math.max(0, Math.ceil((end - now.getTime()) / 86_400_000))
}

/** The first `max` members for the home card's avatar row, and how many are left over. */
export function previewMembers(members, max = 6) {
  const list = Array.isArray(members) ? members : []
  return { shown: list.slice(0, max), more: Math.max(0, list.length - max) }
}

/**
 * The teammates' activity box on home (V5-w), from `teams_activity`:
 * `hidden` unless teams are switched on **and** the answer has members —
 * the server sends `enabled: false` and no items while `teams.enabled` is
 * off (production until stage 3), and no items without a seat, so home
 * stays exactly as it was. `list` otherwise.
 */
export function activityState(answer) {
  if (!answer || answer.enabled !== true) return 'hidden'
  if (!Array.isArray(answer.items) || answer.items.length === 0) return 'hidden'
  return 'list'
}

/**
 * What one row says: `practicing` («… در حال تمرین است») wins over the
 * day's XP; `xp` («… امروز ۲۵ امتیاز گرفت»); `idle` when neither.
 * `practicingNow` is a boolean the server computed — no time is ever sent.
 */
export function activityKind(item) {
  if (item?.practicingNow === true) return 'practicing'
  if ((item?.xpToday ?? 0) > 0) return 'xp'
  return 'idle'
}

/** The rows the home box shows (the server already sorts: practising first, then by XP today), and how many are left over. */
export function activityRows(items, max = 5) {
  const list = Array.isArray(items) ? items : []
  return { shown: list.slice(0, max), more: Math.max(0, list.length - max) }
}

/**
 * Whether the feed offers the «هم‌تیمی‌ها» tab: only with a team to show —
 * teams switched on and a seat this month. Otherwise the tab row is the
 * one every learner has today (`following · top · new`).
 */
export function hasTeammatesTab(answer) {
  return teamCardState(answer) === 'team'
}
